const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://www.robolog.co.in';

// Django enforces CSRF on POST/DELETE when a session cookie is present
// (e.g. when logged into /admin in the same browser), so mutating
// requests must echo the csrftoken cookie back in a header.
function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

function csrfHeader() {
  const token = getCookie('csrftoken');
  return token ? { 'X-CSRFToken': token } : {};
}

// Auth API (Django session auth)
export const authAPI = {
  login: async (username, password) => {
    // Ensure the csrftoken cookie is set before the login POST.
    await fetch(`${API_BASE_URL}/api/auth/csrf/`, { credentials: 'include' });
    const response = await fetch(`${API_BASE_URL}/api/auth/login/`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...csrfHeader(),
      },
      body: JSON.stringify({ username, password }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Invalid username or password');
    }
    return response.json();
  },

  logout: async () => {
    const response = await fetch(`${API_BASE_URL}/api/auth/logout/`, {
      method: 'POST',
      credentials: 'include',
      headers: { ...csrfHeader() },
    });
    return response.ok;
  },

  me: async () => {
    const response = await fetch(`${API_BASE_URL}/api/auth/me/`, {
      credentials: 'include',
    });
    if (!response.ok) return { authenticated: false };
    return response.json();
  },
};

// Certificate API
export const certificateAPI = {
  // New certificate engine creation
  create: async (data) => {
    const response = await fetch(`${API_BASE_URL}/api/certificates/generate-certificate/`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...csrfHeader(),
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create certificate');
    }
    return response.json();
  },

  verify: async (certId) => {
    const response = await fetch(`${API_BASE_URL}/api/certificates/verify/?cert=${certId}`);
    const data = await response.json();
    if (!response.ok) {
      return { verified: false, ...data };
    }
    return data;
  },

  getAll: async () => {
    const response = await fetch(`${API_BASE_URL}/api/certificates/`, { credentials: 'include' });
    if (!response.ok) throw new Error('Failed to fetch certificates');
    return response.json();
  },

  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/api/certificates/${id}/`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { ...csrfHeader() },
    });
    if (!response.ok) throw new Error('Failed to delete certificate');
    return response.status === 204 ? { success: true } : response.json();
  },

  downloadPDF: (id) => {
    window.open(`${API_BASE_URL}/api/certificates/${id}/download/`, '_blank');
  }
};

// ... Letter API
export const letterAPI = {
  getAll: async () => {
    const response = await fetch(`${API_BASE_URL}/api/documents/letters/`, { credentials: 'include' });
    if (!response.ok) throw new Error('Failed to fetch letters');
    return response.json();
  },

  create: async (data) => {
    const response = await fetch(`${API_BASE_URL}/api/documents/letters/`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...csrfHeader(),
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create letter');
    }
    return response.json();
  },

  downloadPDF: (id) => {
    window.open(`${API_BASE_URL}/api/documents/letters/${id}/download/`, '_blank');
  },

  previewPDF: (id) => {
    window.open(`${API_BASE_URL}/api/documents/letters/${id}/preview/`, '_blank');
  },

  delete: async (id) => {
    const response = await fetch(`${API_BASE_URL}/api/documents/letters/${id}/`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { ...csrfHeader() },
    });
    if (!response.ok) throw new Error('Failed to delete letter');
    return response.status === 204 ? { success: true } : response.json();
  },
};

// Verification API (Legacy Support)
export const verificationAPI = {
  verifyCertificate: async (certNo) => {
    const response = await fetch(`${API_BASE_URL}/api/verify/certificate/?cert=${certNo}`);
    const data = await response.json();
    if (!response.ok) {
      return { success: false, ...data };
    }
    return { success: true, ...data };
  },

  verifyLetter: async (refNo) => {
    const response = await fetch(`${API_BASE_URL}/api/verify/letter/?ref=${refNo}`);
    const data = await response.json();
    if (!response.ok) {
      return { success: false, ...data };
    }
    return { success: true, ...data };
  },
};
